import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Card, CardBody, CardSubhead, CardTitle } from '@spark/ui-components/components/Card';
import JanelaDescargaHint, { CardTitleRow } from './JanelaDescargaHint';

const SERIES_CONFIG = [
  {
    key: 'criticos',
    label: 'Criticos',
    color: '#e53935',
    radius: 74,
    keys: ['criticos', 'Criticos', 'critico', 'Critico', 'criticosTotal', 'CriticosTotal']
  },
  {
    key: 'alertas',
    label: 'Alertas',
    color: '#f39c12',
    radius: 52,
    keys: ['alertas', 'Alertas', 'alerta', 'Alerta', 'moderados', 'Moderados']
  },
  {
    key: 'normais',
    label: 'No prazo',
    color: '#2fb65d',
    radius: 30,
    keys: ['normais', 'Normais', 'noPrazo', 'NoPrazo', 'semAtraso', 'SemAtraso']
  }
];

const pickFirstValue = (source, keys, fallback = null) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }

  return fallback;
};

const normalizeArrayResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.registros)) return data.registros;
  if (Array.isArray(data?.dados)) return data.dados;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.resultado)) return data.resultado;
  return null;
};

const normalizeAtrasos = (response) => {
  const data = response?.data || response || {};
  const list = normalizeArrayResponse(data);

  if (list) {
    return SERIES_CONFIG.map((serie, index) => {
      const item = list[index] || {};
      return {
        ...serie,
        label: pickFirstValue(item, ['label', 'Label', 'nome', 'Nome', 'criticidade', 'Criticidade'], serie.label),
        value: Number(pickFirstValue(item, ['valor', 'Valor', 'total', 'Total', 'quantidade', 'Quantidade'], 0)) || 0
      };
    });
  }

  return SERIES_CONFIG.map((serie) => ({
    ...serie,
    value: Number(pickFirstValue(data, serie.keys, 0)) || 0
  }));
};

const AtrasoDescargaCard = ({
  data,
  loading
}) => {
  const series = useMemo(() => normalizeAtrasos(data), [data]);
  const maxValue = Math.max(...series.map(item => item.value), 1);

  return (
    <Card>
      <CardBody>
        <CardTitleRow>
          <CardTitle>Atraso de descarga</CardTitle>
          <JanelaDescargaHint />
        </CardTitleRow>
        <CardSubhead>Divisao por criticidade do tempo de descarga</CardSubhead>

        <ChartArea>
          {loading ? (
            <EmptyState>Carregando metricas...</EmptyState>
          ) : (
            <RingSvg viewBox="0 0 200 200" aria-hidden="true">
              {series.map((serie) => {
                const circumference = 2 * Math.PI * serie.radius;
                const progress = Math.max(0.08, Math.min(1, serie.value / maxValue));
                const dashOffset = circumference * (1 - progress);

                return (
                  <g key={serie.key}>
                    <circle
                      cx="100"
                      cy="100"
                      r={serie.radius}
                      fill="none"
                      stroke="#dce8f6"
                      strokeWidth="11"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r={serie.radius}
                      fill="none"
                      stroke={serie.color}
                      strokeWidth="11"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={dashOffset}
                      transform="rotate(-90 100 100)"
                    />
                  </g>
                );
              })}
            </RingSvg>
          )}
        </ChartArea>

        <LegendFooter>
          {series.map((serie) => (
            <LegendItem key={serie.key}>
              <LegendDot color={serie.color} />
              <span>{serie.label}: {serie.value}</span>
            </LegendItem>
          ))}
        </LegendFooter>
      </CardBody>
    </Card>
  );
};

const ChartArea = styled.div`
  height: 270px;
  margin-top: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RingSvg = styled.svg`
  width: min(190px, 100%);
  height: auto;
`;

const LegendFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  margin: 18px -20px -20px;
  padding: 12px 20px 18px;
  border-top: 1px solid #d8dde3;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
`;

const LegendDot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ color }) => color};
  display: inline-block;
  flex: 0 0 14px;
`;

const EmptyState = styled.div`
  width: 100%;
  height: 270px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  background: #f8f9fa;
  border: 1px dashed #d8dde3;
`;

export default AtrasoDescargaCard;
